import React, { useEffect, useState } from "react";
import axios from "axios";
import { useLang } from "../context/LangContext";
import Layout from "../components/Layout";
import { Bot, Plus, Trash2, Globe, FileText, Upload, Brain, Eye, X, ExternalLink, Pencil } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function KbDetailModal({ item, onClose }) {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/knowledge/${item.id}`)
      .then(({ data }) => setContent(data))
      .finally(() => setLoading(false));
  }, [item.id]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.45)" }}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-start justify-between p-5 border-b border-slate-100">
          <div>
            <h3 className="font-semibold text-slate-900 text-base">{item.title}</h3>
            {item.source_url && (
              <a href={item.source_url} target="_blank" rel="noreferrer"
                className="text-xs text-blue-500 hover:underline flex items-center gap-1 mt-1">
                <ExternalLink size={11} /> {item.source_url}
              </a>
            )}
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700 transition-colors ml-4 flex-shrink-0">
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5">
          {loading ? (
            <div className="text-center py-10 text-slate-400 text-sm">Yükleniyor...</div>
          ) : (
            <pre className="text-sm text-slate-700 whitespace-pre-wrap font-sans leading-relaxed">
              {content?.content || "İçerik bulunamadı."}
            </pre>
          )}
        </div>

        <div className="p-4 border-t border-slate-100 flex justify-end">
          <button onClick={onClose} className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-sm font-medium transition-colors">
            Kapat
          </button>
        </div>
      </div>
    </div>
  );
}

function KbEditModal({ item, onClose, onSaved }) {
  const [title, setTitle] = useState(item.title);
  const [content, setContent] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const canEditContent = ["text", "md", "txt"].includes(item.kb_type);

  useEffect(() => {
    axios.get(`${API}/knowledge/${item.id}`)
      .then(({ data }) => setContent(data.content || ""))
      .finally(() => setLoading(false));
  }, [item.id]);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { title };
      if (canEditContent) payload.content = content;
      const { data } = await axios.put(`${API}/knowledge/${item.id}`, payload);
      onSaved(data);
      onClose();
    } catch {}
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.45)" }}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between p-5 border-b border-slate-100">
          <h3 className="font-semibold text-slate-900 text-base flex items-center gap-2">
            <Pencil size={16} className="text-blue-500" /> Bilgi Tabanı Düzenle
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700 transition-colors">
            <X size={20} />
          </button>
        </div>

        {loading ? (
          <div className="flex-1 flex items-center justify-center text-slate-400 text-sm py-10">Yükleniyor...</div>
        ) : (
          <form onSubmit={handleSave} className="flex flex-col flex-1 overflow-hidden">
            <div className="flex-1 overflow-y-auto p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Başlık</label>
                <input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="w-full h-10 rounded-lg border border-slate-200 px-3 text-sm focus:outline-none focus:border-blue-400"
                  required
                  data-testid="edit-kb-title-input"
                />
              </div>
              {canEditContent ? (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">İçerik</label>
                  <textarea
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-blue-400 resize-none font-mono"
                    rows={12}
                    required
                    data-testid="edit-kb-content-input"
                  />
                </div>
              ) : (
                <div className="p-3 bg-amber-50 border border-amber-100 rounded-lg text-xs text-amber-700">
                  {item.kb_type === "url" ? "URL içeriği düzenlenemez. Silip yeniden ekleyebilirsiniz." : "Bu dosya türünün içeriği düzenlenemez. Yalnızca başlık değiştirilebilir."}
                </div>
              )}
            </div>
            <div className="p-4 border-t border-slate-100 flex justify-end gap-2">
              <button type="button" onClick={onClose} className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-sm font-medium transition-colors">
                İptal
              </button>
              <button type="submit" disabled={saving} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-60" data-testid="save-edit-kb-btn">
                {saving ? "Kaydediliyor..." : "Kaydet"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

export default function AISettings() {
  const { t } = useLang();
  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState(null);
  const [kb, setKb] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddText, setShowAddText] = useState(false);
  const [showAddUrl, setShowAddUrl] = useState(false);
  const [textForm, setTextForm] = useState({ title: "", content: "" });
  const [urlForm, setUrlForm] = useState({ title: "", url: "" });
  const [saving, setSaving] = useState(false);
  const [adding, setAdding] = useState(false);
  const [tab, setTab] = useState("kb");
  const [selectedKb, setSelectedKb] = useState(null);
  const [editingKb, setEditingKb] = useState(null);

  useEffect(() => {
    axios.get(`${API}/sites`).then(({ data }) => {
      setSites(data);
      if (data.length > 0) setSelectedSite(data[0]);
    });
  }, []);

  useEffect(() => {
    if (!selectedSite) return;
    setLoading(true);
    axios.get(`${API}/knowledge?site_id=${selectedSite.id}`)
      .then(({ data }) => setKb(data))
      .finally(() => setLoading(false));
  }, [selectedSite]);

  const addTextKb = async (e) => {
    e.preventDefault();
    setAdding(true);
    try {
      const { data } = await axios.post(`${API}/knowledge/text`, {
        site_id: selectedSite.id, ...textForm
      });
      setKb((prev) => [data, ...prev]);
      setTextForm({ title: "", content: "" });
      setShowAddText(false);
    } catch {}
    setAdding(false);
  };

  const addUrlKb = async (e) => {
    e.preventDefault();
    setAdding(true);
    try {
      const { data } = await axios.post(`${API}/knowledge/url`, {
        site_id: selectedSite.id, ...urlForm
      });
      setKb((prev) => [data, ...prev]);
      setUrlForm({ title: "", url: "" });
      setShowAddUrl(false);
    } catch (err) {
      alert(err?.response?.data?.detail || t("common.error"));
    }
    setAdding(false);
  };

  const uploadFile = async (e) => {
    const file = e.target.files[0];
    if (!file || !selectedSite) return;
    const ext = file.name.split('.').pop().toLowerCase();
    const allowed = ['pdf', 'docx', 'md', 'txt'];
    if (!allowed.includes(ext)) {
      alert(`Desteklenmeyen format. Kabul edilenler: ${allowed.join(', ')}`);
      return;
    }
    const formData = new FormData();
    formData.append("file", file);
    formData.append("site_id", selectedSite.id);
    formData.append("title", file.name.replace(/\.[^.]+$/, ""));
    try {
      const { data } = await axios.post(`${API}/knowledge/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      setKb((prev) => [data, ...prev]);
    } catch (err) {
      alert(err?.response?.data?.detail || "Dosya yüklenemedi");
    }
    e.target.value = "";
  };

  const deleteKb = async (id) => {
    await axios.delete(`${API}/knowledge/${id}`);
    setKb((prev) => prev.filter((k) => k.id !== id));
  };

  const saveAiConfig = async (config) => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/sites/${selectedSite.id}`, { ai_config: config });
      setSelectedSite(data);
      setSites((prev) => prev.map((s) => s.id === data.id ? data : s));
    } catch {}
    setSaving(false);
  };

  const uploadAvatar = async (e) => {
    const file = e.target.files[0];
    if (!file || !selectedSite) return;
    const formData = new FormData();
    formData.append("file", file);
    try {
      const { data } = await axios.post(`${API}/sites/${selectedSite.id}/avatar`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      setSelectedSite(prev => ({
        ...prev,
        widget_config: { ...(prev.widget_config || {}), bot_avatar: data.avatar_url }
      }));
    } catch (err) {
      alert(err?.response?.data?.detail || "Avatar yüklenemedi");
    }
    e.target.value = "";
  };

  const saveBotName = async (name) => {
    try {
      const wc = { ...(selectedSite.widget_config || {}), bot_name: name };
      await axios.put(`${API}/sites/${selectedSite.id}`, { widget_config: wc });
      setSelectedSite(prev => ({ ...prev, widget_config: wc }));
    } catch {}
  };

  const KB_TYPES = {
    text: { icon: FileText, color: "text-blue-600 bg-blue-50", label: "Metin" },
    url: { icon: Globe, color: "text-emerald-600 bg-emerald-50", label: "URL" },
    pdf: { icon: Upload, color: "text-orange-600 bg-orange-50", label: "PDF" },
    docx: { icon: FileText, color: "text-indigo-600 bg-indigo-50", label: "Word" },
    md: { icon: FileText, color: "text-purple-600 bg-purple-50", label: "Markdown" },
    txt: { icon: FileText, color: "text-slate-600 bg-slate-50", label: "TXT" },
  };

  return (
    <Layout>
      {selectedKb && <KbDetailModal item={selectedKb} onClose={() => setSelectedKb(null)} />}
      {editingKb && <KbEditModal item={editingKb} onClose={() => setEditingKb(null)} onSaved={(updated) => { setKb(prev => prev.map(k => k.id === updated.id ? updated : k)); }} />}
      <div className="p-8 max-w-5xl mx-auto">
        <div className="mb-8 animate-fade-in-up">
          <h1 className="font-heading text-3xl font-bold text-slate-900">{t("ai.title")}</h1>
          <p className="text-slate-500 mt-1">AI chatbot ayarları ve bilgi tabanı yönetimi</p>
        </div>

        {/* Site selector */}
        {sites.length > 1 && (
          <div className="mb-6 animate-fade-in-up">
            <div className="flex gap-2 flex-wrap">
              {sites.map((s) => (
                <button
                  key={s.id}
                  onClick={() => setSelectedSite(s)}
                  className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors ${
                    selectedSite?.id === s.id
                      ? "bg-blue-600 text-white border-blue-600"
                      : "bg-white text-slate-700 border-slate-200 hover:border-blue-300"
                  }`}
                  data-testid={`site-select-${s.id}`}
                >
                  {s.name}
                </button>
              ))}
            </div>
          </div>
        )}

        {!selectedSite ? (
          <div className="text-center py-16 text-slate-400">
            <Bot size={40} className="mx-auto mb-3 text-slate-300" />
            <p>Önce bir site ekleyin</p>
          </div>
        ) : (
          <>
            {/* AI Config Card */}
            <div className="bg-white rounded-2xl border border-slate-100 p-6 mb-6 animate-fade-in-up">
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center">
                    <Brain size={20} className="text-blue-600" />
                  </div>
                  <div>
                    <h2 className="font-semibold text-slate-900">AI Konfigürasyonu</h2>
                    <p className="text-xs text-slate-500">Chatbot davranışını yapılandırın</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm text-slate-600">{t("ai.enable")}</span>
                  <button
                    onClick={() => saveAiConfig({ ...selectedSite.ai_config, enabled: !selectedSite.ai_config?.enabled })}
                    className={`w-12 h-6 rounded-full transition-colors relative ${selectedSite.ai_config?.enabled ? "bg-blue-600" : "bg-slate-300"}`}
                    data-testid="ai-enable-toggle"
                  >
                    <div className={`w-5 h-5 bg-white rounded-full absolute top-0.5 transition-transform ${selectedSite.ai_config?.enabled ? "translate-x-6" : "translate-x-0.5"}`} />
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">{t("ai.model")}</label>
                  <select
                    value={selectedSite.ai_config?.model || "gpt-4o"}
                    onChange={(e) => saveAiConfig({ ...selectedSite.ai_config, model: e.target.value })}
                    className="w-full h-11 rounded-xl border border-slate-200 px-4 text-sm focus:outline-none focus:border-blue-400 bg-white"
                    data-testid="model-select"
                  >
                    <optgroup label="OpenAI">
                      <option value="gpt-4o">GPT-4o</option>
                      <option value="gpt-4o-mini">GPT-4o Mini (Hızlı)</option>
                      <option value="gpt-5.1">GPT-5.1 (En Güçlü)</option>
                    </optgroup>
                    <optgroup label="Claude (Anthropic)">
                      <option value="claude-haiku-5-5">Claude Haiku 5.5 (Hızlı)</option>
                      <option value="claude-sonnet-5-5">Claude Sonnet 5.5 (Dengeli)</option>
                      <option value="claude-opus-5-5">Claude Opus 5.5 (En Güçlü)</option>
                    </optgroup>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    {t("ai.temperature")}: <strong>{selectedSite.ai_config?.temperature || 0.7}</strong>
                  </label>
                  <input
                    type="range" min="0" max="1" step="0.1"
                    value={selectedSite.ai_config?.temperature || 0.7}
                    onChange={(e) => saveAiConfig({ ...selectedSite.ai_config, temperature: parseFloat(e.target.value) })}
                    className="w-full mt-2 accent-blue-600"
                    data-testid="temperature-input"
                  />
                </div>
              </div>

              <div className="mt-5">
                <label className="block text-sm font-medium text-slate-700 mb-1.5">{t("ai.system_prompt")}</label>
                <textarea
                  key={selectedSite.id}
                  defaultValue={selectedSite.ai_config?.system_prompt || ""}
                  onBlur={(e) => saveAiConfig({ ...selectedSite.ai_config, system_prompt: e.target.value })}
                  className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-blue-400 resize-none"
                  rows={4}
                  placeholder="Sen yardımsever bir müşteri destek asistanısın. Müşteri sorularını kibarca ve Türkçe yanıtla..."
                  data-testid="system-prompt-textarea"
                />
                <p className="text-xs text-slate-400 mt-1">Boş bırakırsanız otomatik prompt kullanılır</p>
              </div>

              {/* Bot Kimliği */}
              <div className="mt-5 pt-5 border-t border-slate-100">
                <h3 className="text-sm font-semibold text-slate-700 mb-4">Bot Kimliği</h3>
                <div className="flex items-center gap-5">
                  {/* Avatar */}
                  <div className="flex flex-col items-center gap-2">
                    <div className="w-16 h-16 rounded-full border-2 border-slate-200 overflow-hidden bg-blue-50 flex items-center justify-center">
                      {selectedSite.widget_config?.bot_avatar
                        ? <img src={selectedSite.widget_config.bot_avatar} alt="Avatar" className="w-full h-full object-cover" />
                        : <Bot size={28} className="text-blue-400" />
                      }
                    </div>
                    <label className="text-xs text-blue-600 font-medium cursor-pointer hover:underline" data-testid="upload-avatar-btn">
                      Değiştir
                      <input type="file" accept=".png,.jpg,.jpeg,.webp" onChange={uploadAvatar} className="hidden" />
                    </label>
                  </div>
                  {/* Bot adı */}
                  <div className="flex-1">
                    <label className="block text-xs font-medium text-slate-600 mb-1">Bot Adı</label>
                    <input
                      key={selectedSite.id}
                      type="text"
                      defaultValue={selectedSite.widget_config?.bot_name || selectedSite.name + " AI"}
                      onBlur={(e) => saveBotName(e.target.value)}
                      className="w-full h-9 rounded-lg border border-slate-200 px-3 text-sm focus:outline-none focus:border-blue-400"
                      placeholder="Netlen AI"
                      data-testid="bot-name-input"
                    />
                    <p className="text-xs text-slate-400 mt-1">Widget'ta AI mesajlarında görünür</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Knowledge Base */}
            <div className="bg-white rounded-2xl border border-slate-100 p-6 animate-fade-in-up">
              <div className="flex items-center justify-between mb-5">
                <div>
                  <h2 className="font-semibold text-slate-900">{t("ai.knowledge_base")}</h2>
                  <p className="text-xs text-slate-500 mt-0.5">AI'ın yanıt vermesi için bilgi ekleyin</p>
                </div>
                <div className="flex gap-2">
                  <label
                    htmlFor="file-upload"
                    className="flex items-center gap-1.5 px-3 py-2 bg-orange-50 text-orange-700 text-xs font-medium rounded-lg hover:bg-orange-100 transition-colors cursor-pointer"
                    data-testid="upload-file-btn"
                    title="PDF, Word (.docx), Markdown (.md), TXT"
                  >
                    <Upload size={13} /> Dosya Yükle
                    <span className="text-orange-400 ml-0.5">PDF · Word · MD</span>
                    <input id="file-upload" type="file" accept=".pdf,.docx,.md,.txt" onChange={uploadFile} className="hidden" />
                  </label>
                  <button
                    onClick={() => { setShowAddUrl(!showAddUrl); setShowAddText(false); }}
                    className="flex items-center gap-1.5 px-3 py-2 bg-emerald-50 text-emerald-700 text-xs font-medium rounded-lg hover:bg-emerald-100 transition-colors"
                    data-testid="add-url-btn"
                  >
                    <Globe size={13} /> {t("ai.add_url")}
                  </button>
                  <button
                    onClick={() => { setShowAddText(!showAddText); setShowAddUrl(false); }}
                    className="flex items-center gap-1.5 px-3 py-2 bg-blue-50 text-blue-700 text-xs font-medium rounded-lg hover:bg-blue-100 transition-colors"
                    data-testid="add-text-btn"
                  >
                    <Plus size={13} /> {t("ai.add_text")}
                  </button>
                </div>
              </div>

              {showAddText && (
                <form onSubmit={addTextKb} className="mb-4 p-4 bg-blue-50/50 rounded-xl border border-blue-100 animate-fade-in-up">
                  <div className="space-y-3">
                    <input
                      value={textForm.title}
                      onChange={(e) => setTextForm({ ...textForm, title: e.target.value })}
                      className="w-full h-10 rounded-lg border border-slate-200 px-3 text-sm focus:outline-none focus:border-blue-400"
                      placeholder={t("ai.title_field")}
                      required
                      data-testid="kb-title-input"
                    />
                    <textarea
                      value={textForm.content}
                      onChange={(e) => setTextForm({ ...textForm, content: e.target.value })}
                      className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-blue-400 resize-none"
                      placeholder={t("ai.content_field")}
                      rows={5}
                      required
                      data-testid="kb-content-input"
                    />
                    <div className="flex gap-2">
                      <button type="submit" disabled={adding} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-60" data-testid="save-kb-text-btn">
                        {adding ? t("common.loading") : t("common.save")}
                      </button>
                      <button type="button" onClick={() => setShowAddText(false)} className="px-4 py-2 bg-white border border-slate-200 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-50 transition-colors">
                        {t("common.cancel")}
                      </button>
                    </div>
                  </div>
                </form>
              )}

              {showAddUrl && (
                <form onSubmit={addUrlKb} className="mb-4 p-4 bg-emerald-50/50 rounded-xl border border-emerald-100 animate-fade-in-up">
                  <div className="space-y-3">
                    <input
                      value={urlForm.title}
                      onChange={(e) => setUrlForm({ ...urlForm, title: e.target.value })}
                      className="w-full h-10 rounded-lg border border-slate-200 px-3 text-sm focus:outline-none focus:border-emerald-400"
                      placeholder={t("ai.title_field")}
                      required
                      data-testid="kb-url-title-input"
                    />
                    <input
                      value={urlForm.url}
                      onChange={(e) => setUrlForm({ ...urlForm, url: e.target.value })}
                      className="w-full h-10 rounded-lg border border-slate-200 px-3 text-sm focus:outline-none focus:border-emerald-400"
                      placeholder="https://..."
                      type="url"
                      required
                      data-testid="kb-url-input"
                    />
                    <div className="flex gap-2">
                      <button type="submit" disabled={adding} className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 transition-colors disabled:opacity-60" data-testid="save-kb-url-btn">
                        {adding ? "URL çekiliyor..." : t("common.save")}
                      </button>
                      <button type="button" onClick={() => setShowAddUrl(false)} className="px-4 py-2 bg-white border border-slate-200 text-slate-700 rounded-lg text-sm font-medium">
                        {t("common.cancel")}
                      </button>
                    </div>
                  </div>
                </form>
              )}

              {loading ? (
                <div className="text-center py-8 text-slate-400 text-sm">{t("common.loading")}</div>
              ) : kb.length === 0 ? (
                <div className="text-center py-12">
                  <Brain size={32} className="mx-auto text-slate-300 mb-3" />
                  <p className="text-slate-500 text-sm">{t("ai.no_kb")}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {kb.map((item) => {
                    const typeInfo = KB_TYPES[item.kb_type] || KB_TYPES.text;
                    const Icon = typeInfo.icon;
                    return (
                      <div key={item.id} className="flex items-start gap-3 p-4 bg-slate-50 rounded-xl border border-slate-100 group" data-testid={`kb-item-${item.id}`}>
                        <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${typeInfo.color}`}>
                          <Icon size={16} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-sm text-slate-900">{item.title}</span>
                            <span className={`text-xs px-1.5 py-0.5 rounded ${typeInfo.color}`}>{typeInfo.label}</span>
                          </div>
                          <p className="text-xs text-slate-500 mt-0.5 truncate">{item.content_preview}</p>
                        </div>
                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={() => setSelectedKb(item)}
                            className="text-blue-400 hover:text-blue-600 p-1 transition-colors"
                            title="İçeriği görüntüle"
                            data-testid={`view-kb-${item.id}`}
                          >
                            <Eye size={15} />
                          </button>
                          <button
                            onClick={() => setEditingKb(item)}
                            className="text-slate-400 hover:text-indigo-600 p-1 transition-colors"
                            title="Düzenle"
                            data-testid={`edit-kb-${item.id}`}
                          >
                            <Pencil size={15} />
                          </button>
                          <button
                            onClick={() => deleteKb(item.id)}
                            className="text-red-400 hover:text-red-600 p-1 transition-colors"
                            data-testid={`delete-kb-${item.id}`}
                          >
                            <Trash2 size={15} />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}
